type Breakpoint = "desktop" | "tablet" | "mobile";
type NodeType = "text" | "image" | "video" | "button" | "container";

interface EditorFrame {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface EditorNode {
    id: string;
    type: NodeType;
    label: string;
    content: Record<string, string | number>;
    frames: Record<Breakpoint, EditorFrame>;
    style: {
        fontSize: number;
        fontWeight: number;
        color: string;
        background?: string;
        textAlign: "left" | "center" | "right";
        borderRadius: number;
    };
}

interface EditorDocument {
    version: number;
    canvas: {
        width: number;
        height: number;
        background: string;
    };
    breakpoints: Record<Breakpoint, { label: string; width: number }>;
    nodes: EditorNode[];
}

const BREAKPOINTS: Breakpoint[] = ["desktop", "tablet", "mobile"];

function defaultFrame(bp: Breakpoint): EditorFrame {
    if (bp === "mobile") return { x: 24, y: 80, w: 320, h: 84 };
    if (bp === "tablet") return { x: 64, y: 96, w: 420, h: 84 };
    return { x: 120, y: 120, w: 420, h: 96 };
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function normalizeDoc(raw: unknown): EditorDocument {
    const fallback: EditorDocument = {
        version: 1,
        canvas: { width: 1200, height: 640, background: "#ffffff" },
        breakpoints: {
            desktop: { label: "Desktop", width: 1200 },
            tablet: { label: "Tablet", width: 768 },
            mobile: { label: "Mobile", width: 390 },
        },
        nodes: [],
    };
    if (!raw || typeof raw !== "object") return fallback;
    const obj = raw as Partial<EditorDocument>;
    const canvas = obj.canvas ?? fallback.canvas;
    const nodes = Array.isArray(obj.nodes) ? obj.nodes : [];
    return {
        ...fallback,
        canvas: {
            width: Number(canvas.width) || fallback.canvas.width,
            height: Number(canvas.height) || fallback.canvas.height,
            background: String(canvas.background || fallback.canvas.background),
        },
        nodes: nodes
            .filter((node): node is EditorNode => !!node && typeof node === "object" && typeof node.id === "string")
            .map((node) => ({
                id: node.id,
                type: (["text", "image", "video", "button", "container"].includes(node.type) ? node.type : "text") as NodeType,
                label: String(node.label || node.id),
                content: node.content && typeof node.content === "object" ? node.content : {},
                frames: {
                    desktop: { ...defaultFrame("desktop"), ...(node.frames?.desktop ?? {}) },
                    tablet: { ...defaultFrame("tablet"), ...(node.frames?.tablet ?? {}) },
                    mobile: { ...defaultFrame("mobile"), ...(node.frames?.mobile ?? {}) },
                },
                style: {
                    fontSize: Number(node.style?.fontSize) || 20,
                    fontWeight: Number(node.style?.fontWeight) || 400,
                    color: String(node.style?.color || "#111827"),
                    background: String(node.style?.background || ""),
                    textAlign: (["left", "center", "right"].includes(String(node.style?.textAlign))
                        ? node.style?.textAlign
                        : "left") as "left" | "center" | "right",
                    borderRadius: Number(node.style?.borderRadius) || 0,
                },
            })),
    };
}

class CmsSiteEditorController {
    readonly #root: HTMLElement;
    readonly #documentField: HTMLTextAreaElement;
    readonly #artboard: HTMLElement;
    readonly #inspector: HTMLElement;
    readonly #layers: HTMLElement | null;
    #doc: EditorDocument;
    #bp: Breakpoint = "desktop";
    #selectedId = "";
    #drag:
        | {
              id: string;
              mode: "move" | "resize";
              startX: number;
              startY: number;
              frame: EditorFrame;
          }
        | null = null;

    constructor(root: HTMLElement) {
        this.#root = root;
        this.#documentField = root.querySelector<HTMLTextAreaElement>("[data-cms-site-editor-document]")!;
        this.#artboard = root.querySelector<HTMLElement>("[data-cms-site-editor-artboard]")!;
        this.#inspector = root.querySelector<HTMLElement>("[data-cms-site-editor-inspector]")!;
        this.#layers = root.querySelector<HTMLElement>("[data-cms-site-editor-layers]");
        const raw = this.#documentField.value || root.dataset.initialDocument || "{}";
        this.#doc = normalizeDoc(JSON.parse(raw));
    }

    init(): void {
        this.#root.querySelectorAll<HTMLButtonElement>("[data-cms-site-editor-add]").forEach((button) => {
            button.addEventListener("click", () => this.#addNode((button.dataset.cmsSiteEditorAdd || "text") as NodeType));
        });
        this.#root.querySelectorAll<HTMLButtonElement>("[data-cms-site-editor-breakpoint]").forEach((button) => {
            button.addEventListener("click", () => this.#setBreakpoint((button.dataset.cmsSiteEditorBreakpoint || "desktop") as Breakpoint));
        });
        this.#root.querySelector<HTMLButtonElement>("[data-cms-site-editor-fit]")?.addEventListener("click", () => this.#fit());
        this.#root.querySelector<HTMLFormElement>("form")?.addEventListener("submit", () => this.#sync());
        window.addEventListener("pointermove", (event) => this.#onPointerMove(event));
        window.addEventListener("pointerup", () => {
            this.#drag = null;
        });
        this.#render();
    }

    #addNode(type: NodeType): void {
        const id = `${type}-${Date.now().toString(36)}`;
        const label = type.charAt(0).toUpperCase() + type.slice(1);
        const node: EditorNode = {
            id,
            type,
            label,
            content:
                type === "image"
                    ? { src: "" }
                    : type === "video"
                      ? { src: "" }
                      : type === "button"
                        ? { text: "Button", href: "#" }
                        : { text: "새 텍스트" },
            frames: {
                desktop: defaultFrame("desktop"),
                tablet: defaultFrame("tablet"),
                mobile: defaultFrame("mobile"),
            },
            style: {
                fontSize: type === "button" ? 16 : 24,
                fontWeight: type === "text" ? 600 : 500,
                color: "#111827",
                background: type === "button" ? "#0b7dd8" : "",
                textAlign: "left",
                borderRadius: type === "button" ? 6 : 0,
            },
        };
        this.#doc.nodes.push(node);
        this.#selectedId = id;
        this.#render();
    }

    #setBreakpoint(bp: Breakpoint): void {
        if (!BREAKPOINTS.includes(bp)) return;
        this.#bp = bp;
        this.#root.querySelectorAll("[data-cms-site-editor-breakpoint]").forEach((button) => {
            button.classList.toggle("is-active", (button as HTMLElement).dataset.cmsSiteEditorBreakpoint === bp);
        });
        this.#render();
    }

    #render(): void {
        this.#renderArtboard();
        this.#renderLayers();
        this.#renderInspector();
        this.#sync();
    }

    #renderArtboard(): void {
        this.#artboard.innerHTML = "";
        this.#artboard.style.width = `${this.#doc.breakpoints[this.#bp].width}px`;
        this.#artboard.style.height = `${this.#doc.canvas.height}px`;
        this.#artboard.style.background = this.#doc.canvas.background;
        this.#doc.nodes.forEach((node) => this.#artboard.appendChild(this.#nodeEl(node)));
    }

    #nodeEl(node: EditorNode): HTMLElement {
        const frame = node.frames[this.#bp];
        const el = document.createElement("button");
        el.type = "button";
        el.className = "admin-cms-site-editor__node";
        el.classList.toggle("is-selected", node.id === this.#selectedId);
        el.dataset.nodeId = node.id;
        el.style.left = `${frame.x}px`;
        el.style.top = `${frame.y}px`;
        el.style.width = `${frame.w}px`;
        el.style.height = `${frame.h}px`;
        el.style.fontSize = `${node.style.fontSize}px`;
        el.style.fontWeight = String(node.style.fontWeight);
        el.style.color = node.style.color;
        el.style.textAlign = node.style.textAlign;
        el.style.borderRadius = `${node.style.borderRadius}px`;
        if (node.style.background) el.style.background = node.style.background;
        el.addEventListener("pointerdown", (event) => this.#startDrag(event, node.id, "move"));
        el.addEventListener("click", () => {
            this.#selectedId = node.id;
            this.#render();
        });

        if (node.type === "image") {
            const src = String(node.content.src || "");
            el.textContent = src ? "Image" : "이미지";
            if (src) {
                el.style.backgroundImage = `url("${src}")`;
                el.style.backgroundSize = "cover";
                el.style.backgroundPosition = "center";
            }
        } else if (node.type === "video") {
            el.textContent = "Video";
        } else {
            el.textContent = String(node.content.text || node.label);
        }

        const handle = document.createElement("span");
        handle.className = "admin-cms-site-editor__resize";
        handle.addEventListener("pointerdown", (event) => this.#startDrag(event, node.id, "resize"));
        el.appendChild(handle);
        return el;
    }

    #startDrag(event: PointerEvent, id: string, mode: "move" | "resize"): void {
        event.preventDefault();
        event.stopPropagation();
        const node = this.#find(id);
        if (!node) return;
        this.#selectedId = id;
        this.#drag = {
            id,
            mode,
            startX: event.clientX,
            startY: event.clientY,
            frame: { ...node.frames[this.#bp] },
        };
        this.#renderInspector();
    }

    #onPointerMove(event: PointerEvent): void {
        if (!this.#drag) return;
        const node = this.#find(this.#drag.id);
        if (!node) return;
        const dx = event.clientX - this.#drag.startX;
        const dy = event.clientY - this.#drag.startY;
        const frame = node.frames[this.#bp];
        if (this.#drag.mode === "move") {
            frame.x = Math.round(this.#drag.frame.x + dx);
            frame.y = Math.round(this.#drag.frame.y + dy);
        } else {
            frame.w = clamp(Math.round(this.#drag.frame.w + dx), 30, 2400);
            frame.h = clamp(Math.round(this.#drag.frame.h + dy), 30, 2400);
        }
        this.#renderArtboard();
        this.#renderLayers();
        this.#sync();
    }

    #renderInspector(): void {
        const node = this.#find(this.#selectedId);
        if (!node) {
            this.#inspector.innerHTML = `<p class="admin-cms-site-editor__empty">요소를 선택하세요.</p>`;
            return;
        }
        const frame = node.frames[this.#bp];
        this.#inspector.innerHTML = `
            <label>라벨<input data-field="label" value="${this.#esc(node.label)}"></label>
            <label>내용<textarea data-field="text">${this.#esc(String(node.content.text || node.content.src || ""))}</textarea></label>
            <label>링크/URL<input data-field="href" value="${this.#esc(String(node.content.href || node.content.src || ""))}"></label>
            <div class="admin-cms-site-editor__quad">
              <label>X<input type="number" data-frame="x" value="${frame.x}"></label>
              <label>Y<input type="number" data-frame="y" value="${frame.y}"></label>
              <label>W<input type="number" data-frame="w" value="${frame.w}"></label>
              <label>H<input type="number" data-frame="h" value="${frame.h}"></label>
            </div>
            <div class="admin-cms-site-editor__quad">
              <label>Size<input type="number" data-style="fontSize" value="${node.style.fontSize}"></label>
              <label>Weight<input type="number" data-style="fontWeight" value="${node.style.fontWeight}"></label>
              <label>Radius<input type="number" data-style="borderRadius" value="${node.style.borderRadius}"></label>
              <label>Color<input data-style="color" value="${this.#esc(node.style.color)}"></label>
            </div>
            <label>정렬<select data-style="textAlign">
              <option value="left"${node.style.textAlign === "left" ? " selected" : ""}>Left</option>
              <option value="center"${node.style.textAlign === "center" ? " selected" : ""}>Center</option>
              <option value="right"${node.style.textAlign === "right" ? " selected" : ""}>Right</option>
            </select></label>
            <button type="button" class="admin-btn admin-btn--danger admin-btn--sm" data-action="remove">삭제</button>
        `;
        this.#inspector.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input,textarea,select").forEach((input) => {
            input.addEventListener("input", () => this.#applyInspector(node, input));
        });
        this.#inspector.querySelector<HTMLButtonElement>("[data-action='remove']")?.addEventListener("click", () => {
            this.#doc.nodes = this.#doc.nodes.filter((item) => item.id !== node.id);
            this.#selectedId = "";
            this.#render();
        });
    }

    #applyInspector(node: EditorNode, input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): void {
        const field = input.dataset.field;
        const frameKey = input.dataset.frame as keyof EditorFrame | undefined;
        const styleKey = input.dataset.style as keyof EditorNode["style"] | undefined;
        if (field === "label") node.label = input.value;
        if (field === "text") {
            if (node.type === "image" || node.type === "video") node.content.src = input.value;
            else node.content.text = input.value;
        }
        if (field === "href") {
            if (node.type === "image" || node.type === "video") node.content.src = input.value;
            else node.content.href = input.value;
        }
        if (frameKey) node.frames[this.#bp][frameKey] = Number(input.value) || 0;
        if (styleKey) {
            const numeric = ["fontSize", "fontWeight", "borderRadius"].includes(styleKey);
            (node.style as Record<string, string | number>)[styleKey] = numeric ? Number(input.value) || 0 : input.value;
        }
        this.#render();
    }

    #renderLayers(): void {
        if (!this.#layers) return;
        this.#layers.innerHTML = `<strong class="admin-cms-site-editor__panel-title">레이어</strong>`;
        this.#doc.nodes.forEach((node) => {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "admin-cms-site-editor__layer";
            button.classList.toggle("is-active", node.id === this.#selectedId);
            button.textContent = node.label;
            button.addEventListener("click", () => {
                this.#selectedId = node.id;
                this.#render();
            });
            this.#layers?.appendChild(button);
        });
    }

    #fit(): void {
        this.#artboard.scrollIntoView({ block: "nearest", inline: "center" });
    }

    #find(id: string): EditorNode | null {
        return this.#doc.nodes.find((node) => node.id === id) ?? null;
    }

    #sync(): void {
        this.#documentField.value = JSON.stringify(this.#doc);
    }

    #esc(value: string): string {
        return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }
}

export function initCmsSiteEditor(): void {
    document.querySelectorAll<HTMLElement>("[data-cms-site-editor]").forEach((root) => {
        if (root.dataset.cmsSiteEditorBound === "1") return;
        root.dataset.cmsSiteEditorBound = "1";
        try {
            new CmsSiteEditorController(root).init();
        } catch (error) {
            console.error("[CmsSiteEditor] init failed", error);
        }
    });
}
